import styled from '@emotion/styled'
import { BidderCatalog } from '@pubstack/common/src/bidderCatalog'
import { toUpsertPbstckBidderGlobalConfig } from '@pubstack/common/src/pbstckBidderGlobalConfig'
import { FunctionComponent, useCallback, useMemo, useState } from 'react'
import { usePbstckBidderGlobalConfig, useUpdatePbstckBidderGlobalConfig } from '~/api/admin/pbstck-bidder-global-config.api'
import { useCatalog } from '~/api/catalog.api'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { Title_P1 } from '~/components/_styled/titles'
import Button from '~/components/Button'
import { Checkbox } from '@pubstack/ui/components/Checkbox'
import { Flyout } from '~/components/Flyout'
import { BaseInputElement } from '~/components/Input'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Spinner } from '~/components/Spinner'
import { useToast } from '~/components/Toast/useToasts'
import { toggleValueInArray } from '~/utils/array'

const SectionDescription = styled.div`
  ${Fonts.P1}
  margin-bottom: 1.5rem;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin-top: 0.5rem;
`

const CheckboxCell = styled.th`
  width: 20px;
  border-top: none;
  border-bottom: 1px solid ${Colors.Platinum};

  .mantine-Checkbox-root {
    height: 100%;
    display: flex;
  }
`

const CheckboxDataCell = styled.td`
  width: 20px;
  border-top: 1px solid ${Colors.Platinum};
  border-bottom: 1px solid ${Colors.Platinum};
  border-left: none;
  border-right: none;

  .mantine-Checkbox-root {
    height: 100%;
    display: flex;
  }
`

const Th = styled.th`
  border-top: none;
  border-bottom: 1px solid ${Colors.Platinum};
  padding: 0.5rem;
  text-align: left;
  font-weight: 500;
`

const Td = styled.td`
  border-top: 1px solid ${Colors.Platinum};
  border-bottom: 1px solid ${Colors.Platinum};
  border-left: none;
  border-right: none;
  padding: 0.25rem 0.5rem;
  height: 38px;
  cursor: pointer;
`

const NoResultsTd = styled(Td)`
  ${Fonts.colors.SlateGrey}
  font-style: italic;
`

const Info = styled.span`
  ${Fonts.P3}
  color: ${Colors.SlateGrey};
  font-style: italic;
  font-weight: 500;
  margin-left: 0.5rem;
`

// Tracking labels
const SELECT_ALL_TRACKING_LABEL = 'common-admin-spark-config-select-all'
const SELECT_BIDDER_TRACKING_LABEL = 'common-admin-spark-config-select-bidder'
const VALIDATE_TRACKING_LABEL: TrackingLabel = 'common-admin-spark-config-save'

type BiddersSelectionTableProps = {
  enabledBidderCodes: string[]
  pbstckBidderCompatibleBidders: BidderCatalog[]
  onBidderSelectionChange: (selectedBidderCodes: string[]) => void
}

export const BiddersSelectionTable: FunctionComponent<BiddersSelectionTableProps> = ({ enabledBidderCodes, pbstckBidderCompatibleBidders, onBidderSelectionChange }) => {
  // State
  const [selectedBidderCodes, setSelectedBidderCodes] = useState<string[]>(enabledBidderCodes || [])
  // Resync selection when the enabledBidderCodes prop changes ("previous value" pattern).
  const [prevEnabledBidderCodes, setPrevEnabledBidderCodes] = useState(enabledBidderCodes)
  if (enabledBidderCodes !== prevEnabledBidderCodes) {
    setPrevEnabledBidderCodes(enabledBidderCodes)
    setSelectedBidderCodes(enabledBidderCodes || [])
  }
  const [searchTerm, setSearchTerm] = useState<string>('')

  const filteredBidders = useMemo(() => pbstckBidderCompatibleBidders.filter((e) => e.displayName.toLowerCase().includes(searchTerm)), [pbstckBidderCompatibleBidders, searchTerm])

  const isAllSelected = selectedBidderCodes.length === pbstckBidderCompatibleBidders.length

  const toggleSelectAll = useCallback(
    (checked: boolean) => {
      // `enabledBidderCodes` holds catalog **ids** — they are matched against `BidderSeat.bidderCode`,
      // which is an id, and are what seats get created from.
      const updatedSelectedBidderCodes = checked ? pbstckBidderCompatibleBidders.map((e) => e.id) : []
      setSelectedBidderCodes(updatedSelectedBidderCodes)
      onBidderSelectionChange(updatedSelectedBidderCodes)
    },
    [pbstckBidderCompatibleBidders, onBidderSelectionChange]
  )

  const toggleSelectRow = useCallback(
    (code: string) => {
      const updatedSelectedBidderCodes = toggleValueInArray(selectedBidderCodes, code)
      setSelectedBidderCodes(updatedSelectedBidderCodes)
      onBidderSelectionChange(updatedSelectedBidderCodes)
    },
    [selectedBidderCodes, onBidderSelectionChange]
  )

  return (
    <>
      <BaseInputElement name="search" type="text" iconLeft="search" labelIsPlaceholder label="Search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      <Table>
        <thead>
          <tr>
            <CheckboxCell>
              <Checkbox
                checked={isAllSelected}
                indeterminate={selectedBidderCodes.length > 0 && !isAllSelected}
                onClick={() => toggleSelectAll(!isAllSelected)}
                trackingLabel={isAllSelected ? `${SELECT_ALL_TRACKING_LABEL}-unset` : `${SELECT_ALL_TRACKING_LABEL}-set`}
              />
            </CheckboxCell>
            <Th>
              SSP name
              <Info>
                {selectedBidderCodes.length} SSP{selectedBidderCodes.length > 1 ? 's' : ''} selected
              </Info>
            </Th>
          </tr>
        </thead>
        <tbody>
          {filteredBidders.length > 0 ? (
            filteredBidders.map((row) => (
              <tr
                key={row.id}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  toggleSelectRow(row.id)
                }}
              >
                <CheckboxDataCell>
                  <Checkbox
                    checked={!!selectedBidderCodes.find((e) => e === row.id)}
                    onClick={() => toggleSelectRow(row.id)}
                    trackingLabel={selectedBidderCodes.find((e) => e === row.id) ? `${SELECT_BIDDER_TRACKING_LABEL}-unset` : `${SELECT_BIDDER_TRACKING_LABEL}-set`}
                  />
                </CheckboxDataCell>
                <Td>{row.displayName}</Td>
              </tr>
            ))
          ) : (
            <tr>
              <NoResultsTd colSpan={3}>No results</NoResultsTd>
            </tr>
          )}
        </tbody>
      </Table>
    </>
  )
}

type Props = {
  onClose: () => void
}

export const PbstckBidderSettingsFlyout: FunctionComponent<Props> = ({ onClose }) => {
  // API Hooks
  const { data: pbstckBidderGlobalConfig, isLoading: isLoadingPbstckBidderGlobalConfig } = usePbstckBidderGlobalConfig()
  const { data: pbstckBidderCompatibleBidders, isLoading: isLoadingPbstckBidderCompatibleBidders } = useCatalog('pbs', { isPbstckBidderCompatible: true })
  const { mutateAsync: updatePbstckBidderGlobalConfig, isPending } = useUpdatePbstckBidderGlobalConfig()

  // State
  const [localEnabledBidderCodes, setLocalEnabledBidderCodes] = useState<string[]>(pbstckBidderGlobalConfig?.enabledBidderCodes || [])
  // Resync selection when the global config prop changes ("previous value" pattern).
  const [prevGlobalConfigCodes, setPrevGlobalConfigCodes] = useState(pbstckBidderGlobalConfig?.enabledBidderCodes)
  if (pbstckBidderGlobalConfig?.enabledBidderCodes !== prevGlobalConfigCodes) {
    setPrevGlobalConfigCodes(pbstckBidderGlobalConfig?.enabledBidderCodes)
    setLocalEnabledBidderCodes(pbstckBidderGlobalConfig?.enabledBidderCodes || [])
  }

  // Hooks
  const toast = useToast()

  const handleBidderSelectionChange = (selectedBidderCodes: string[]) => {
    setLocalEnabledBidderCodes(selectedBidderCodes)
  }

  const handleValidateClick = async () => {
    await updatePbstckBidderGlobalConfig(toUpsertPbstckBidderGlobalConfig(localEnabledBidderCodes, pbstckBidderCompatibleBidders))
    toast.success('Spark settings updated successfully!')
    onClose()
  }

  return (
    <Flyout.Content>
      <Flyout.Title>Spark settings for all scopes</Flyout.Title>
      <Flyout.Body>
        {isLoadingPbstckBidderGlobalConfig || isLoadingPbstckBidderCompatibleBidders ? (
          <Spinner />
        ) : (
          <>
            <Title_P1>Bidder activation cross-scopes</Title_P1>
            <SectionDescription>Enable SSP partners on all the scopes. Once enabled, all current and new scopes with a Spark contract will have a new seat.</SectionDescription>
            <BiddersSelectionTable enabledBidderCodes={localEnabledBidderCodes} pbstckBidderCompatibleBidders={pbstckBidderCompatibleBidders} onBidderSelectionChange={handleBidderSelectionChange} />
          </>
        )}
      </Flyout.Body>
      <Flyout.Actions>
        <Button
          disabled={isPending || isLoadingPbstckBidderGlobalConfig}
          iconName={isPending ? 'pubstack' : undefined}
          iconSpin={isPending}
          onClick={handleValidateClick}
          trackingLabel={VALIDATE_TRACKING_LABEL}
        >
          {isPending ? 'Validating...' : 'Validate'}
        </Button>
      </Flyout.Actions>
    </Flyout.Content>
  )
}
